import React, { lazy } from "react";
import { BrowserRouter, useLocation } from "react-router";
import { Helmet } from "react-helmet";
import { Button } from "semantic-ui-react";

import { ToastContainer } from "react-toastify";

import { prep_env } from "./prep_env";
import prep_ionic from "./prep_ionic";

// import "styles/semantic-sass/semantic-ui.scss";
// import "styles/themes/index.scss";
// import "styles/tailwind.css";
// import "styles/video-react.css";

import Page from "./Page";
import withSuspense from "xAppLib/libs/withSuspense";
import { useUser } from "xAppLib/Hooks";
import logger from "xAppLib/libs/logger";
import Logo from "views/UIelems/Logo";

import { UserProvider } from "xAppLib/context";
import { AppProvider, AppConsumer } from "xAppLib/context";
import { UserDataProvider } from "xAppLib/context/UserDataContext";

import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import AppHistory from "./xAppLib/App/AppHistory";
import { NotificationsProvider } from "./notifications/context/NotificationsContext";
import { EcommerceDataProvider } from "./xAppLib/context";
import LegacyMessageAdapter from "./notifications/view/app/LegacyMessageAdapter";
import { AppleAppStoreButton, GooglePlayStoreButton } from "views/UIelems/v2/AppStoreButtons";
import {AppUpdateNudgeContext} from "@iscr/xAppLib/context/AppUpdateNudgeContext";
import {QueryProvider} from "@iscr/xAppLib/context/QueryProvider";
import {MimsRegistrationProvider} from "@iscr/mims/context/MimsRegistrationProvider";


setTimeout(() => import("styles/others.scss"), 0)

const BoothApp = withSuspense(lazy(() => import("views/booth/kiosk/BoothApp")));

/**
 * The telehealth booth touchscreen replaces the patient shell rather than rendering inside it.
 *
 * `Page` brings the header, side menu, footer, support widgets and idle screen - every one of which is a way off this
 * screen or onto another origin, and none of which belongs on a locked kiosk in a shopping centre. Swapping at this
 * level means the booth never mounts them at all, rather than mounting them and hiding them with CSS.
 *
 * Driven by the device's `booth` claim rather than by the route, so a booth stays in its own shell wherever it
 * navigates and `BoothApp` owns its own routing. That is what the pod needs: it opens the app at `/`, and only the
 * first run after sign-in gets redirected, so a route-based switch would drop it on the patient homepage thereafter.
 */
function PageOrBooth() {
	// Subscribing to the user is what re-renders this when claims arrive - they are not present on first paint.
	useUser();
	useLocation();

	return app.settings.is_booth ? <BoothApp /> : <Page />;
}

const public_sites = [
	"www.instantscripts.com.au",
	"app.instantscripts.com.au",
	"instantcosmetics.com.au",
	"instantcosmetic.com.au",
	"hivprevent.com.au",
	"thegreendoc.com.au",
	"goodleaf.com.au",
	"goodleaf.au",
	"benicura.com.au",
	"benicura.au",
	"benicura.com",
	"haiku.health",
	"blsclinics.com.au",
];

class App extends React.Component {
	constructor(props) {
		super(props);

		prep_env(_ => {
			app.state.loaded = true;
			this.setState({ env_loaded: true });
		});
		this.state = { env_loaded: false, newVersion: false };
	}

	//	------------------------	------------------------	------------------------

	componentDidMount() {
		this.ionicCleanup = prep_ionic();
		// window.addEventListener('hashchange', () => {app.history.push(app.history.location.hash.substring(1))})

		this.histCleanup = app.history.listen((location, action) => {
			// Remove trailing slashes
			const pathname = location.pathname?.replace(/\/+$/, "");
			try {
				let p = pathname?.split("/").pop();
				if (p == "") p = "home";
				if (this.last_path != p) {
					if (this.last_path) {
						document.body.classList.remove(this.last_path);
					}
					document.body.classList.add(p);
					this.last_path = p;
				}
			} catch (e) {
				//console.log(e)
			}
			if (this.prev_loc != location && action === "PUSH") {
				(app.settings.is_ionic ? document.getElementById("app") : window).scrollTo(0, 0);
			}
			this.prev_loc = location;
		}, true);
	}

	componentWillUnmount() {
		this.ionicCleanup?.();
		this.histCleanup?.();
	}

	//	------------------------	------------------------	------------------------

	componentDidCatch(error, errorInfo) {
		logger.report_error("ERROR in componentDidCatch", "error", error, errorInfo);
	}

	static getDerivedStateFromError(error) {
		if (error.name == "ChunkLoadError") return { newVersion: true };
		return { error };
	}

	//	------------------------	------------------------	------------------------

	render() {
		const pp_prod_id = "AcP9n6G_C0I_dbtIbCkEzosOHasFGNv98oArQSagXRInWO_ep_zP2hgSaRs4vCqVrYHBxfyxuRc0za2F";
		const pp_dev_id = "Ad_9EBwljSiKKnWSrorQXbwOfFZl6knenT_Yt1fgCPsMagmpptnlaC2q5L8QF0dsGiJfsZEUxZpRjIoG";

		const paypalInitialOptions = {
			"client-id": app.settings.pp_prod ? pp_prod_id : pp_dev_id,
			currency: "AUD",
			intent: "authorize",
		};

		if (this.state.error) {
			return this.render_error();
		}

		const old_ionic = app.settings.is_ionic && window.Capacitor?.isNativePlatform() !== true;

		const NON_INDEXABLE_ROUTES = ["/medications", "/profile", "/products/bpm", "/med", "/subscr"];
		const shouldIndex = location => {
			if (
				public_sites.includes(location.hostname) &&
				!NON_INDEXABLE_ROUTES.some(route => location.pathname.includes(route))
			) {
				return <meta name="robots" content="index" />;
			}
			return <meta name="robots" content="noindex" />;
		};

		return (
			<BrowserRouter>
				<AppHistory />
				<PayPalScriptProvider options={paypalInitialOptions}>
					<AppProvider>
						<UserProvider>
                        <AppUpdateNudgeContext>
                            <QueryProvider>
                            <MimsRegistrationProvider>
							<UserDataProvider>
								<EcommerceDataProvider>
									<NotificationsProvider>
										<LegacyMessageAdapter />
										<Helmet titleTemplate={`%s – ${app.runtime.name}`}>
											{shouldIndex(window.location)}
										</Helmet>
										<ToastContainer
											enableMultiContainer
											containerId={"default"}
											position="top-center"
											autoClose={5000}
											hideProgressBar={false}
											newestOnTop={false}
											closeOnClick
											rtl={false}
											pauseOnFocusLoss
											draggable
											pauseOnHover
											style={{
												zIndex: 2147483006,
											}}
										/>
										<ToastContainer
											enableMultiContainer
											containerId={"actionable"}
											autoClose={false}
											closeOnClick={false}
											position="bottom-left"
											newestOnTop
											rtl={false}
											draggable
											limit={5}
											style={{
												zIndex: 2147483006,
											}}
										/>
										<AppConsumer>
											{({ site_status }) => {
												if (old_ionic) return this.render_update_native();
												else if (site_status.maint_mode)
													return this.render_maint_mode(site_status.maint_msg);
												else
													return (
														<>
															{this.state.newVersion || site_status.newVersion ? (
																this.render_new_version()
															) : (
																    <PageOrBooth />
															)}
														</>
													);
											}}
										</AppConsumer>
									</NotificationsProvider>
								</EcommerceDataProvider>
							</UserDataProvider>
                            </MimsRegistrationProvider>
                            </QueryProvider>
                        </AppUpdateNudgeContext>
						</UserProvider>
					</AppProvider>
				</PayPalScriptProvider>
			</BrowserRouter>
		);
	}

	render_new_version() {
		return (
			<div
				style={{
					position: "absolute",
					top: "0",
					left: "0",
					width: "100vw",
					height: "100vh",
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
					background: "white",
					zIndex: 10000,
				}}
			>
				<Logo />
				<br />
				<h3>New {app.runtime.name} app is available. </h3>
				<Button
					color="green"
					size="big"
					as={app.settings.is_ionic && "a"}
					target={app.settings.is_ionic && "upd"}
					href={
						app.settings.is_ionic &&
						((app.settings.is_ionic_ios && "https://apps.apple.com/au/app/instantscripts/id1456598384") ||
							(app.settings.is_ionic_android &&
								"https://play.google.com/store/apps/details?id=au.com.instant"))
					}
					onClick={_ => window.location.reload()}
				>
					Click to update to the latest version
				</Button>
			</div>
		);
	}

	render_update_native() {
		return (
			<div
				style={{
					position: "absolute",
					top: "0",
					left: "0",
					width: "100vw",
					height: "100vh",
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
					background: "white",
					zIndex: 10000,
				}}
			>
				<Logo />
				<br />
				<h3 className="text-center">
					There is a new version of {app.runtime.name} available.
					<br />
					Please download the latest update to keep using {app.runtime.name}.
				</h3>
				<div className="flex justify-center gap-3">
					<AppleAppStoreButton />
					<GooglePlayStoreButton />
				</div>
			</div>
		);
	}

	render_error() {
		return (
			<div
				style={{
					position: "absolute",
					top: "0",
					left: "0",
					width: "100vw",
					height: "100vh",
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
					background: "white",
					zIndex: 10000,
				}}
			>
				<Logo />
				<br />
				<h3>{app.dev_env ? this.state.error.message : `Something went wrong.`}</h3>
				<p>We have been notified of the problem.</p>
				<p>Please reload and try again.</p>
				<Button color="green" size="big" onClick={() => window.location.reload()}>
					Reload
				</Button>
				{app.dev_env && (
					<pre style={{ fontSize: "10px", maxWidth: "100vw", padding: "2em", overflow: "scroll" }}>
						{this.state.error.stack}
					</pre>
				)}
			</div>
		);
	}

	render_maint_mode(msg) {
		return (
			<div
				style={{
					position: "absolute",
					top: "0",
					left: "0",
					width: "100vw",
					height: "100vh",
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
					background: "white",
					zIndex: 10000,
				}}
			>
				<Logo />
				<br />
				<h3>{app.runtime.name} is currently unavailable. </h3>
				<h3>{msg || "Please try again soon."}</h3>
				{/* <Button outline  
					as={app.settings.is_ionic && 'a'} 
					target={app.settings.is_ionic && 'upd'} 
					href={app.settings.is_ionic && (app.settings.is_ionic_ios && 'https://apps.apple.com/au/app/instantscripts/id1456598384' || app.settings.is_ionic_android && 'https://play.google.com/store/apps/details?id=au.com.instant')}
					onClick={_=>window.location.reload()}
				>Click to refresh</Button>  */}
			</div>
		);
	}
}

export default App;
